import React, { Suspense, lazy, useEffect } from 'react';
import SectionLoading from './components/SectionLoading';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { cleanupGoogleSignInOverlays } from './utils/googleSignInCleanup';
import { wakeUpBackend } from './config';
import { trackPageView } from './utils/analytics';
import { LabTestCartProvider } from './context/LabTestCartContext';
import { MedicineCartProvider } from './context/MedicineCartContext';
import { DentalTestCartProvider } from './context/DentalTestCartContext';
import { PrescriptionUploadProvider } from './context/PrescriptionUploadContext';
import { FamilyMembersProvider } from './context/FamilyMembersContext';
import Navigation from './components/Navigation';
import { COMPANY_OFFICE_ADDRESS_LINES } from './constants/companyInfo';
import { SITE_NAME } from './constants/seo';
import { Helmet } from 'react-helmet-async';
import Home from './pages/Home';
import About from './pages/About';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import NotFound from './pages/NotFound';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import ForBusiness from './pages/ForBusiness';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import ViewBackup from './pages/ViewBackup';
import Login from './pages/Login';
import Otp from "./pages/Otp";
import Proceed from './pages/Proceed';
import Signup from './pages/Signup';
import CompleteProfile from './pages/CompleteProfile';
import OnboardingFamily from './pages/OnboardingFamily';
import OpsLogin from './pages/OpsLogin';
import DoctorLogin from './pages/DoctorLogin';
import PortalOtp from './pages/PortalOtp';
import './App.css';

const DASHBOARD_LOADING_CLASS = 'section-loading--dashboard';

/** Route-level code splitting; each page gets its own Suspense so parent layouts stay mounted while it loads. */
function lazyPage(loader, loadingClassName = '') {
  const Page = lazy(loader);
  return function LazyPage(props) {
    return (
      <Suspense fallback={<SectionLoading className={loadingClassName} label="Loading page" />}>
        <Page {...props} />
      </Suspense>
    );
  };
}

const dashboardPage = (loader) => lazyPage(loader, DASHBOARD_LOADING_CLASS);

const Dashboard = dashboardPage(() => import('./pages/Dashboard'));
const DashboardHome = dashboardPage(() => import('./pages/dashboard/DashboardHome'));
const DashboardPurchaseHistory = dashboardPage(() => import('./pages/dashboard/DashboardPurchaseHistory'));
const DashboardAppointments = dashboardPage(() => import('./pages/dashboard/DashboardAppointments'));
const DashboardClinicAppointment = dashboardPage(() => import('./pages/dashboard/DashboardClinicAppointment'));
const DashboardClinicSearchResults = dashboardPage(() => import('./pages/dashboard/DashboardClinicSearchResults'));
const DashboardDoctorProfile = dashboardPage(() => import('./pages/dashboard/DashboardDoctorProfile'));
const DashboardAppointmentSlot = dashboardPage(() => import('./pages/dashboard/DashboardAppointmentSlot'));
const DashboardAppointmentConfirm = dashboardPage(() => import('./pages/dashboard/DashboardAppointmentConfirm'));
const DashboardMedicineOrders = dashboardPage(() => import('./pages/dashboard/DashboardMedicineOrders'));
const DashboardMedicineSearch = dashboardPage(() => import('./pages/dashboard/DashboardMedicineSearch'));
const DashboardMedicineReview = dashboardPage(() => import('./pages/dashboard/DashboardMedicineReview'));
const DashboardMedicinePaymentReturn = dashboardPage(() => import('./pages/dashboard/DashboardMedicinePaymentReturn'));
const DashboardMedicineOrderStatus = dashboardPage(() => import('./pages/dashboard/DashboardMedicineOrderStatus'));
const DashboardOnlineConsults = dashboardPage(() => import('./pages/dashboard/DashboardOnlineConsults'));
const DashboardLabTests = dashboardPage(() => import('./pages/dashboard/DashboardLabTests'));
const DashboardLabTestSelect = dashboardPage(() => import('./pages/dashboard/DashboardLabTestSelect'));
const DashboardDentalTests = dashboardPage(() => import('./pages/dashboard/DashboardDentalTests'));
const DashboardDentalConsultDoctors = dashboardPage(() => import('./pages/dashboard/DashboardDentalConsultDoctors'));
const DashboardDentalConsultCentres = dashboardPage(() => import('./pages/dashboard/DashboardDentalConsultCentres'));
const DashboardDentalConsultSelectUser = dashboardPage(() => import('./pages/dashboard/DashboardDentalConsultSelectUser'));
const DashboardDentalConsultSlot = dashboardPage(() => import('./pages/dashboard/DashboardDentalConsultSlot'));
const DashboardDentalConsultConfirm = dashboardPage(() => import('./pages/dashboard/DashboardDentalConsultConfirm'));
const DashboardDentalTestSelect = dashboardPage(() => import('./pages/dashboard/DashboardDentalTestSelect'));
const DashboardDentalTestReview = dashboardPage(() => import('./pages/dashboard/DashboardDentalTestReview'));
const DashboardDentalTestLabs = dashboardPage(() => import('./pages/dashboard/DashboardDentalTestLabs'));
const DashboardDentalTestConfirm = dashboardPage(() => import('./pages/dashboard/DashboardDentalTestConfirm'));
const DashboardDentalTestPaymentReturn = dashboardPage(() => import('./pages/dashboard/DashboardDentalTestPaymentReturn'));
const DashboardDentalPrescription = dashboardPage(() => import('./pages/dashboard/DashboardDentalPrescription'));
const DashboardDentalSlot = dashboardPage(() => import('./pages/dashboard/DashboardDentalSlot'));
const DashboardDentalBookingConfirm = dashboardPage(() => import('./pages/dashboard/DashboardDentalBookingConfirm'));
const DashboardBenefits = dashboardPage(() => import('./pages/dashboard/DashboardBenefits'));
const DashboardOrderHistory = dashboardPage(() => import('./pages/dashboard/DashboardOrderHistory'));
const DashboardAppointmentHistory = dashboardPage(() => import('./pages/dashboard/DashboardAppointmentHistory'));
const DashboardOrderStatus = dashboardPage(() => import('./pages/dashboard/DashboardOrderStatus'));
const DashboardSelectPrescription = dashboardPage(() => import('./pages/dashboard/DashboardSelectPrescription'));
const DashboardLabTestReview = dashboardPage(() => import('./pages/dashboard/DashboardLabTestReview'));
const DashboardLabTestLabs = dashboardPage(() => import('./pages/dashboard/DashboardLabTestLabs'));
const DashboardLabTestConfirm = dashboardPage(() => import('./pages/dashboard/DashboardLabTestConfirm'));
const DashboardLabTestPaymentReturn = dashboardPage(() => import('./pages/dashboard/DashboardLabTestPaymentReturn'));
const DashboardAhcPackageLabs = dashboardPage(() => import('./pages/dashboard/DashboardAhcPackageLabs'));
const DashboardAhcPackageDetails = dashboardPage(() => import('./pages/dashboard/DashboardAhcPackageDetails'));
const DashboardAhcPackageList = dashboardPage(() => import('./pages/dashboard/DashboardAhcPackageList'));
const DashboardPrescriptionDetails = dashboardPage(() => import('./pages/dashboard/DashboardPrescriptionDetails'));
const AddMember = dashboardPage(() => import('./pages/dashboard/AddMember'));
const OpsProtectedRoute = lazyPage(() => import('./components/OpsProtectedRoute'));
const OpsLayout = lazyPage(() => import('./pages/ops/OpsLayout'));
const OpsDashboard = lazyPage(() => import('./pages/ops/OpsDashboard'));
const OpsAnalytics = lazyPage(() => import('./pages/ops/OpsAnalytics'));
const OpsEmployeeMarketing = lazyPage(() => import('./pages/ops/OpsEmployeeMarketing'));
const OpsServiceAreas = lazyPage(() => import('./pages/ops/OpsServiceAreas'));
const OpsBookings = lazyPage(() => import('./pages/ops/OpsBookings'));
const OpsBookingsLab = lazyPage(() => import('./pages/ops/bookings/OpsBookingsLab'));
const OpsBookingsDental = lazyPage(() => import('./pages/ops/bookings/OpsBookingsDental'));
const OpsBookingsClinic = lazyPage(() => import('./pages/ops/bookings/OpsBookingsClinic'));
const OpsBookingsPharmacy = lazyPage(() => import('./pages/ops/bookings/OpsBookingsPharmacy'));
const OpsBookingsTele = lazyPage(() => import('./pages/ops/bookings/OpsBookingsTele'));
const OpsBookingsMental = lazyPage(() => import('./pages/ops/bookings/OpsBookingsMental'));
const OpsOneMgRedirects = lazyPage(() => import('./pages/ops/OpsOneMgRedirects'));
const OpsDigitization = lazyPage(() => import('./pages/ops/OpsDigitization'));
const OpsMasterDashboard = lazyPage(() => import('./pages/ops/OpsMasterDashboard'));
const OpsTeamRoute = lazyPage(() => import('./pages/ops/OpsTeamRoute'));
const OpsUsersRoute = lazyPage(() => import('./pages/ops/OpsUsersRoute'));
const OpsLogsRoute = lazyPage(() => import('./pages/ops/OpsLogsRoute'));
const OpsLogsLayout = lazyPage(() => import('./pages/ops/OpsLogsLayout'));
const OpsLogsSystem = lazyPage(() => import('./pages/ops/OpsLogsSystem'));
const OpsLogsActivity = lazyPage(() => import('./pages/ops/OpsLogsActivity'));
const OpsDoctorsRoute = lazyPage(() => import('./pages/ops/OpsDoctorsRoute'));
const OpsLabPayments = lazyPage(() => import('./pages/ops/OpsLabPayments'));
const OpsSponsors = lazyPage(() => import('./pages/ops/OpsSponsors'));
const OpsPartners = lazyPage(() => import('./pages/ops/OpsPartners'));
const DoctorProtectedRoute = lazyPage(() => import('./components/DoctorProtectedRoute'));
const DoctorLayout = lazyPage(() => import('./pages/doctor/DoctorLayout'));
const DoctorPendingDigitization = lazyPage(() => import('./pages/doctor/DoctorPendingDigitization'));
const DoctorCompletedDigitization = lazyPage(() => import('./pages/doctor/DoctorCompletedDigitization'));
const DoctorCanceledDigitization = lazyPage(() => import('./pages/doctor/DoctorCanceledDigitization'));
const DoctorTotalEarning = lazyPage(() => import('./pages/doctor/DoctorTotalEarning'));
const DoctorDigitizationEdit = lazyPage(() => import('./pages/doctor/DoctorDigitizationEdit'));
const DoctorPartnerTestMapping = lazyPage(() => import('./pages/doctor/DoctorPartnerTestMapping'));
const DoctorVirtualId = lazyPage(() => import('./pages/doctor/DoctorVirtualId'));
const DoctorChangePassword = lazyPage(() => import('./pages/doctor/DoctorChangePassword'));

function AppContent() {
  const location = useLocation();
  const isLoginFlow =
    location.pathname === '/login' ||
    location.pathname === '/login/otp' ||
    location.pathname === '/login/proceed' ||
    location.pathname === '/login/signup' ||
    location.pathname === '/login/complete-profile' ||
    location.pathname === '/login/onboarding-family' ||
    location.pathname === '/ops/login' ||
    location.pathname === '/ops/login/otp' ||
    location.pathname === '/doctor/login' ||
    location.pathname === '/doctor/login/otp' ||
    location.pathname === '/admin/login';
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isOps = location.pathname.startsWith('/ops') && location.pathname !== '/ops/login';
  const isDoctor = location.pathname.startsWith('/doctor') && location.pathname !== '/doctor/login';

  // Warm API once on first load so Contact/Login requests are fast
  useEffect(() => {
    wakeUpBackend();
  }, []);

  useEffect(() => {
    if (isLoginFlow) return;
    cleanupGoogleSignInOverlays();
  }, [location.pathname, isLoginFlow]);

  // Delay so react-helmet-async has applied the new page's <title>.
  useEffect(() => {
    const id = setTimeout(() => trackPageView(location.pathname), 100);
    return () => clearTimeout(id);
  }, [location.pathname]);

  return (
    <div className="App">
      {(isDashboard || isOps || isDoctor) && (
        <Helmet>
          <title>{SITE_NAME}</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      )}
      {!isLoginFlow && !isDashboard && !isOps && !isDoctor && <Navigation />}
      <main
        className={
          isLoginFlow || isDashboard || isOps || isDoctor
            ? 'main-content main-content-full' +
              (isDashboard ? ' main-content-dashboard' : '') +
              (isOps ? ' main-content-ops' : '') +
              (isDoctor ? ' main-content-doctor' : '')
            : 'main-content'
        }
      >
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/for-business" element={<ForBusiness />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/view-backup" element={<ViewBackup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/login/otp" element={<Otp />} />
            <Route path="/login/proceed" element={<Proceed />} />
            <Route path="/login/signup" element={<Signup />} />
            <Route path="/login/complete-profile" element={<CompleteProfile />} />
            <Route
              path="/login/onboarding-family"
              element={
                <FamilyMembersProvider>
                  <OnboardingFamily />
                </FamilyMembersProvider>
              }
            />
            <Route path="/ops/login" element={<OpsLogin />} />
            <Route path="/ops/login/otp" element={<PortalOtp />} />
            <Route path="/doctor/login" element={<DoctorLogin />} />
            <Route path="/doctor/login/otp" element={<PortalOtp />} />
            <Route path="/admin/login" element={<Navigate to="/ops/login" replace />} />
            <Route path="/admin" element={<Navigate to="/ops" replace />} />
            <Route path="/dashboard" element={<FamilyMembersProvider><LabTestCartProvider><MedicineCartProvider><DentalTestCartProvider><PrescriptionUploadProvider><Dashboard /></PrescriptionUploadProvider></DentalTestCartProvider></MedicineCartProvider></LabTestCartProvider></FamilyMembersProvider>}>
              <Route index element={<DashboardHome />} />
              {/* <Route path="rewards" element={<DashboardRewards />} /> */}
              <Route path="add-member" element={<AddMember />} />
              <Route path="benefits" element={<DashboardBenefits />} />
              <Route path="purchase-history" element={<DashboardPurchaseHistory />} />
              <Route path="appointments" element={<DashboardAppointments />} />
              <Route path="appointments/clinic" element={<DashboardClinicAppointment />} />
              <Route path="appointments/clinic/results" element={<DashboardClinicSearchResults />} />
              <Route path="appointments/clinic/doctor/:doctorId" element={<DashboardDoctorProfile />} />
              <Route path="appointments/clinic/doctor/:doctorId/slot" element={<DashboardAppointmentSlot />} />
              <Route path="appointments/clinic/doctor/:doctorId/confirm" element={<DashboardAppointmentConfirm />} />
              <Route path="medicine-orders" element={<DashboardMedicineOrders />} />
              <Route path="medicine-orders/search" element={<DashboardMedicineSearch />} />
              <Route path="medicine-orders/review" element={<DashboardMedicineReview />} />
              <Route path="medicine-orders/payment-return" element={<DashboardMedicinePaymentReturn />} />
              <Route path="medicine-orders/:orderId/status" element={<DashboardMedicineOrderStatus />} />
              <Route path="online-consults" element={<DashboardOnlineConsults />} />
              <Route path="lab-tests" element={<DashboardLabTests />} />
              <Route path="lab-tests/select" element={<DashboardLabTestSelect />} />
              <Route path="lab-tests/select-prescription" element={<DashboardSelectPrescription />} />
              <Route path="lab-tests/prescription-details" element={<DashboardPrescriptionDetails />} />
              <Route path="lab-tests/review" element={<DashboardLabTestReview />} />
              <Route path="lab-tests/labs" element={<DashboardLabTestLabs />} />
              <Route path="lab-tests/packages" element={<DashboardAhcPackageList />} />
              <Route path="lab-tests/packages/:packageId" element={<DashboardAhcPackageDetails />} />
              <Route path="lab-tests/ahc-packages" element={<DashboardAhcPackageLabs />} />
              <Route path="lab-tests/confirm" element={<DashboardLabTestConfirm />} />
              <Route path="lab-tests/payment-return" element={<DashboardLabTestPaymentReturn />} />
              <Route path="dental-tests" element={<DashboardDentalTests />} />
              <Route path="dental-tests/consult" element={<DashboardDentalConsultDoctors />} />
              <Route path="dental-tests/consult/centres" element={<DashboardDentalConsultCentres />} />
              <Route path="dental-tests/consult/user" element={<DashboardDentalConsultSelectUser />} />
              <Route path="dental-tests/consult/slot" element={<DashboardDentalConsultSlot />} />
              <Route path="dental-tests/consult/confirm" element={<DashboardDentalConsultConfirm />} />
              <Route path="dental-tests/prescription" element={<DashboardDentalPrescription />} />
              <Route path="dental-tests/slots" element={<DashboardDentalSlot />} />
              <Route path="dental-tests/booking-confirm" element={<DashboardDentalBookingConfirm />} />
              <Route path="dental-tests/select" element={<DashboardDentalTestSelect />} />
              <Route path="dental-tests/review" element={<DashboardDentalTestReview />} />
              <Route path="dental-tests/labs" element={<DashboardDentalTestLabs />} />
              <Route path="dental-tests/confirm" element={<DashboardDentalTestConfirm />} />
              <Route path="dental-tests/payment-return" element={<DashboardDentalTestPaymentReturn />} />
              <Route path="order-history" element={<DashboardOrderHistory />} />
              <Route path="appointment-history" element={<DashboardAppointmentHistory />} />
              <Route path="order-status" element={<DashboardOrderStatus />} />
            </Route>
            <Route
              path="/ops"
              element={
                <OpsProtectedRoute>
                  <OpsLayout />
                </OpsProtectedRoute>
              }
            >
              <Route index element={<OpsDashboard />} />
              <Route path="analytics" element={<OpsAnalytics />} />
              <Route path="marketing" element={<OpsEmployeeMarketing />} />
              <Route path="master" element={<OpsMasterDashboard />} />
              <Route path="users" element={<OpsUsersRoute />} />
              <Route path="logs" element={<OpsLogsRoute />}>
                <Route element={<OpsLogsLayout />}>
                  <Route index element={<Navigate to="system" replace />} />
                  <Route path="system" element={<OpsLogsSystem />} />
                  <Route path="activity/:category" element={<OpsLogsActivity />} />
                </Route>
              </Route>
              <Route path="team" element={<OpsTeamRoute />} />
              <Route path="doctors" element={<OpsDoctorsRoute />} />
              <Route path="service-areas" element={<OpsServiceAreas />} />
              <Route path="bookings" element={<OpsBookings />} />
              <Route path="bookings/lab" element={<OpsBookingsLab />} />
              <Route path="bookings/dental" element={<OpsBookingsDental />} />
              <Route path="bookings/clinic" element={<OpsBookingsClinic />} />
              <Route path="bookings/pharmacy" element={<OpsBookingsPharmacy />} />
              <Route path="bookings/tele" element={<OpsBookingsTele />} />
              <Route path="bookings/mental" element={<OpsBookingsMental />} />
              <Route path="1mg-redirects" element={<OpsOneMgRedirects />} />
              <Route path="digitization" element={<OpsDigitization />} />
              <Route path="payments" element={<OpsLabPayments />} />
              <Route path="sponsors" element={<OpsSponsors />} />
              <Route path="partners" element={<OpsPartners />} />
            </Route>
            <Route
              path="/doctor"
              element={
                <DoctorProtectedRoute>
                  <DoctorLayout />
                </DoctorProtectedRoute>
              }
            >
              <Route index element={<Navigate to="pending" replace />} />
              <Route path="pending" element={<DoctorPendingDigitization />} />
              <Route path="completed" element={<DoctorCompletedDigitization />} />
              <Route path="canceled" element={<DoctorCanceledDigitization />} />
              <Route path="earnings" element={<DoctorTotalEarning />} />
              <Route path="test-mapping" element={<DoctorPartnerTestMapping />} />
              <Route path="digitization/:id/edit" element={<DoctorDigitizationEdit />} />
              <Route path="virtual-id" element={<DoctorVirtualId />} />
              <Route path="change-password" element={<DoctorChangePassword />} />
            </Route>
            <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isLoginFlow && !isDashboard && !isOps && !isDoctor && (
        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <div className="footer-brand">
                <h3>Unaffected Healthcare</h3>
                <p>Making quality healthcare affordable for MSME</p>
              </div>
              <div className="footer-links-section">
                <div className="footer-links-column">
                  <h4>Quick Links</h4>
                  <Link to="/about">About Us</Link>
                  <Link to="/for-business">For Business</Link>
                  <Link to="/blog">Blog</Link>
                  <Link to="/faq">FAQ</Link>
                  <Link to="/contact">Contact Us</Link>
                </div>
                <div className="footer-links-column">
                  <h4>Legal</h4>
                  <Link to="/privacy">Privacy</Link>
                  <Link to="/terms">Terms &amp; Conditions</Link>
                </div>
              </div>
              <div className="footer-contact">
                <h4>Registered office</h4>
                <p className="footer-address">
                  {COMPANY_OFFICE_ADDRESS_LINES.join('\n')}
                </p>
              </div>
            </div>
            <div className="footer-bottom">
              <div className="footer-bottom-left">
                <div className="footer-bottom-text">
                  <h3>Unaffected Healthcare</h3>
                  <p>Making quality healthcare affordable for MSME</p>
                  <span className="footer-copy">&copy; 2025 by Unaffected Healthcare LLP</span>
                </div>
              </div>
              <div className="footer-bottom-right">
                <div className="footer-social">
                  <a href="https://www.linkedin.com/company/unaffected-healthcare/posts/?feedView=all" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    <span>LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
  const content = (
    <Router>
      <AppContent />
    </Router>
  );

  if (!GOOGLE_CLIENT_ID) {
    return content;
  }

  return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{content}</GoogleOAuthProvider>;
}

export default App;

